<template>
  <div class="ls-leads-share-contact-details-view--container">
    <span class="ls-leads-share-contact-details-view--heading">
      Additional job details to help a pro price your job
    </span>
    <form novalidate @submit.prevent="submit">
      <div
        v-if="showPreferredTimeOptions"
        class="ls-leads-share-contact-details-view--quote-times-wrapper"
        test-id="preferred-quote-times"
      >
        <div>
          <LsCheckboxes
            v-model="selectedDays"
            track-id="onboarding_leads_share_contact_details_view-quote_days"
            :options="daysOptions"
            name="selectedDays"
            label="When would you like the pro to provide a quote for landscaping jobs?"
            :columns="true"
            :validation="{ required: true }"
            @input="onDayChoiceInput"
          />
        </div>
        <div v-for="(day, idx) in daysOptions" :key="day.value">
          <LsCheckboxes
            v-if="showDayTimeChoices(day.value)"
            v-model="selectedQuoteTimes[idx].times"
            :track-id="`onboarding_leads_share_contact_details_view-quote_times_day_${idx + 1}`"
            :options="timesOptions"
            :name="`selectedQuoteTimes-${idx + 1}`"
            :label="timeOptionLabel(idx)"
            :validation="getTimesValidation(day.value)"
            :columns="true"
          />
        </div>
      </div>
      <div
        :class="[
          'ls-leads-share-contact-details-view--bottom-wrapper',
          { 'margin-top': showPreferredTimeOptions },
        ]"
      >
        <LsCheckboxes
          v-model="shareContact"
          track-id="onboarding_leads_share_contact_details_view-share_contact"
          :options="contactOptions"
          test-id="checkbox"
          name="shareContact"
        />
        <LsButton
          text="continue"
          track-id="onboarding_leads_share_contact_details_view-continue"
          type="submit"
          :disabled="isUpdating"
          :loading="isUpdating"
        />
      </div>
    </form>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { LsButton, LsCheckboxes } from '@/components/common';
import {
  PROFILE__UPDATE_BASIC_INFO,
  ONBOARDING__LEADS_SUBMIT_PREFERRED_QUOTE_TIMES,
} from '@/store/types';
import { lsValidationService } from 'ls-vue-common';
import _ from 'lodash';

export default {
  name: 'OnboardingLeadsShareContactDetailsView',
  $_veeValidate: {
    validator: 'new',
  },
  components: {
    LsButton,
    LsCheckboxes,
  },
  data() {
    return {
      shareContact: true,
      contactOptions: [{ label: 'Give pro my contact information', value: true, boldLabel: true }],
      selectedDays: [],
      selectedQuoteTimes: [],
    };
  },
  computed: {
    ...mapState(['profile', 'onboarding']),

    isUpdating() {
      return this.profile.loadingStatus.isUpdating || this.onboarding.loadingStatus.isUpdating;
    },
    showPreferredTimeOptions() {
      return _.size(this.onboarding.leadsDetails.preferredQuoteTimes);
    },
    daysOptions() {
      const { preferredQuoteTimes } = this.onboarding.leadsDetails;

      const days = _.map(preferredQuoteTimes, (day) => {
        return {
          label: day.label,
          value: day.value,
        };
      });

      return [...days, { label: "I don't need an in-person quote", value: false }];
    },
    timesOptions() {
      const { preferredQuoteTimes } = this.onboarding.leadsDetails;

      return preferredQuoteTimes[0].times;
    },
  },
  mounted() {
    const { preferredQuoteTimes } = this.onboarding.leadsDetails;

    this.selectedQuoteTimes = _.reduce(
      preferredQuoteTimes,
      (result, day) => {
        result.push({
          date: day.value,
          times: [],
        });
        return result;
      },
      []
    );
  },
  methods: {
    ...mapActions([PROFILE__UPDATE_BASIC_INFO, ONBOARDING__LEADS_SUBMIT_PREFERRED_QUOTE_TIMES]),

    timeOptionLabel(idx) {
      return `${this.daysOptions[idx].label} - What time of day do you prefer?`;
    },
    showDayTimeChoices(day) {
      if (day && this.isDaySelected(day)) {
        return true;
      }

      return false;
    },
    isDaySelected(day) {
      return _.includes(this.selectedDays, day);
    },
    onDayChoiceInput(value) {
      const checkedBox = _.last(value);

      this.resetTimes(value, checkedBox);

      this.selectedDays = _.filter(this.selectedDays, (day) => {
        return checkedBox ? day : !day;
      });
    },
    resetTimes(value, checkedBox) {
      // if the date is not in the value array or checkedBox is false,
      // we know we either unchecked the date, or clicked "I don't need an ..."  so we want to reset
      // the times value to empty just for that date or for all dates
      _.forEach(this.selectedQuoteTimes, (day) => {
        if (!_.includes(value, day.date) || !checkedBox) {
          day.times = [];
        }
      });
    },
    getTimesValidation(day) {
      if (this.isDaySelected(day)) {
        return {
          required: true,
        };
      }
      return null;
    },
    async submit() {
      const valid = await this.$validator.validateAll();

      if (!valid) {
        return lsValidationService.scrollToInvalid();
      }

      const calls = [];

      if (!this.shareContact) {
        calls.push(
          this[PROFILE__UPDATE_BASIC_INFO]({
            share_contact_details: this.shareContact,
          })
        );
      }

      const dates = _.reject(this.selectedQuoteTimes, (day) => {
        return !_.size(day.times);
      });

      if (_.size(dates)) {
        calls.push(this[ONBOARDING__LEADS_SUBMIT_PREFERRED_QUOTE_TIMES]({ dates }));
      }

      if (_.size(calls)) {
        await Promise.all(calls);
      }

      this.$router.replace({ name: 'services' });
    },
  },
};
</script>

<style lang="scss" scoped>
.ls-leads-share-contact-details-view--container {
  padding: 60px 70px;
  display: flex;
  flex-direction: column;

  .ls-leads-share-contact-details-view--heading {
    font-weight: 600;
    font-size: 24px;
    color: $primary;
    text-align: center;
    margin-bottom: 40px;
  }

  .ls-leads-share-contact-details-view--quote-times-wrapper {
    border-bottom: 1px solid $primary-light;
    padding-bottom: 4px;

    div:not(:last-child) {
      margin-bottom: 14px;
    }
  }

  .ls-leads-share-contact-details-view--bottom-wrapper {
    &.margin-top {
      margin-top: 30px;
    }
  }

  span {
    font-weight: bold;
  }

  button {
    margin-top: 28px;
  }
}

@media (max-width: $mobile-max-width) {
  .ls-leads-share-contact-details-view--container {
    padding: 0;

    .ls-leads-share-contact-details-view--heading {
      font-size: 20px;
      text-align: center;
    }
  }
}
.themed-app {
  .ls-leads-share-contact-details-view--container {
    .ls-leads-share-contact-details-view--heading {
      color: $theme-color-primary;
    }
    .ls-leads-share-contact-details-view--quote-times-wrapper {
      border-color: $theme-color-primary-light;
    }
  }
}
</style>
